import React, { useState, useCallback } from 'react';
import {
  Box,
  Text,
  Heading,
  Button,
  Radio,
  RadioGroup,
  Divider,
} from '@razorpay/blade/components';
import { TriggerScope } from 'api/types/aiWorkflowTypes';
import { TriggerConfig } from '../../types';
import { TRIGGER_ICONS, TRIGGER_SCOPE_OPTIONS } from '../../constants';
import { BladeTypeAhead } from 'components/ApprovalWorkflows/CreateWorkflow/components/BladeTypeAhead';
import { NamePill } from 'components/ApprovalWorkflows/CreateWorkflow/components/NamePill';
import api from 'api';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { TriggerConfigurationProps, EmployeeSearchItem } from './types';

const TriggerConfiguration = ({
  trigger,
  config,
  onSave,
}: TriggerConfigurationProps) => {
  const [triggerScope, setTriggerScope] = useState<TriggerScope>(config?.triggerScope || 'all_users');
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeSearchItem[]>(() => {
    if (config?.employeeList && config.employeeList.length > 0 && config.employeeNames) {
      return config.employeeList.map((id, index) => ({
        id,
        name: config.employeeNames?.[index] || `Employee ${id}`,
      }));
    }
    return [];
  });

  const orgId = useReduxSelector(loggedInUserSelectors.organizationID);

  const getIcon = (code: string) => {
    return TRIGGER_ICONS[code] || '⚡';
  };

  const employeeSearchFn = useCallback(
    (searchText: string): Promise<EmployeeSearchItem[]> => {
      if (!orgId) return Promise.resolve([]);
      return api.people
        .search({
          search: searchText,
          orgId,
          onlyActive: true,
          useRoleBasedSearch: true,
        })
        .then((searchResults) =>
          searchResults
            .filter((user) => !selectedEmployees.some((emp) => emp.id === user.key))
            .map((user) => ({
              id: user.key,
              name: user.label,
            })),
        );
    },
    [orgId, selectedEmployees],
  );

  const handleEmployeeSelection = (employee: EmployeeSearchItem) => {
    if (!selectedEmployees.some((emp) => emp.id === employee.id)) {
      setSelectedEmployees((prev) => [...prev, employee]);
    }
  };

  const handleRemoveEmployee = (employeeId: number) => {
    setSelectedEmployees((prev) => prev.filter((emp) => emp.id !== employeeId));
  };

  const resultItemView = ({ data }: { data: EmployeeSearchItem }) => (
    <Box display="flex" alignItems="center" gap="spacing.2">
      <Text>{data.name}</Text>
    </Box>
  );

  const handleSave = () => {
    onSave({
      triggerScope,
      employeeList: triggerScope === 'specific' ? selectedEmployees.map((emp) => emp.id) : [],
      employeeNames: triggerScope === 'specific' ? selectedEmployees.map((emp) => emp.name) : [],
    });
  };

  const isSaveDisabled = triggerScope === 'specific' && selectedEmployees.length === 0;

  return (
    <Box display="flex" flexDirection="column" gap="spacing.5">
      <Box display="flex" alignItems="center" gap="spacing.3">
        <Text size="large">{getIcon(trigger.code)}</Text>
        <Heading size="medium">{trigger.name}</Heading>
      </Box>

      <Text color="surface.text.gray.muted">{trigger.description}</Text>

      <Divider />

      <Box display="flex" flexDirection="column" gap="spacing.4">
        <RadioGroup
          label="Apply to"
          value={triggerScope}
          onChange={({ value }) => setTriggerScope(value as TriggerScope)}>
          {TRIGGER_SCOPE_OPTIONS.map((option) => (
            <Radio key={option.value} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </RadioGroup>

        {triggerScope === 'specific' && (
          <Box display="flex" flexDirection="column" gap="spacing.4" padding="1px">
            <BladeTypeAhead<EmployeeSearchItem>
              label=""
              placeholder="Search and add employees"
              searchFn={employeeSearchFn}
              resultItemView={resultItemView}
              onSearchItemClick={handleEmployeeSelection}
            />
            {selectedEmployees.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap="spacing.3">
                {selectedEmployees.map((employee) => (
                  <NamePill
                    key={employee.id}
                    name={employee.name}
                    showCloseIcon={true}
                    onClose={() => handleRemoveEmployee(employee.id)}
                  />
                ))}
              </Box>
            )}
            {selectedEmployees.length === 0 && (
              <Text size="small" color="surface.text.gray.muted">
                Search and select at least one employee to continue.
              </Text>
            )}
          </Box>
        )}
      </Box>

      <Box marginTop="spacing.4">
        <Button onClick={handleSave} isFullWidth isDisabled={isSaveDisabled}>
          Save Configuration
        </Button>
      </Box>
    </Box>
  );
};

export { TriggerConfiguration };
